import { useMemo, useState } from 'react';
import { FileWarning, ShieldAlert, ShieldCheck, Ticket, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { formatSydneyDateShort } from '../../../utils/helpers';
import { HrBarList } from '../components/BarList';
import { HrDataTable } from '../components/DataTable';
import type { HrColumn } from '../components/DataTable';
import { HrColumnChart } from '../components/ColumnChart';
import { HrExportButtons } from '../components/ExportButtons';
import { FilterSelect } from '../components/FilterBar';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  CORE_TICKETS,
  RENEWABLE_NO_EXPIRY,
  countBy,
  downloadCsv,
  downloadXlsx,
  exportFilename,
  holdsWhitecard,
  isSiteLevel,
  next12Months,
  percent,
  qualificationStatusTone,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrQualification } from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

export function QualificationsSection({
  data,
  people,
  filters,
  asOf,
  canExport,
  checkExport,
}: HrSectionProps) {
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [activeTicket, setActiveTicket] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);

  const ids = new Set(people.map((person) => person.id));
  const scoped = data.qualifications.filter((qualification) => ids.has(qualification.personId));
  const filtered = selectedNames.length
    ? scoped.filter((qualification) => selectedNames.includes(qualification.name))
    : scoped;

  const siteStaff = people.filter((person) => isSiteLevel(person.level));
  const matrixStaff = siteStaff.length > 0 ? siteStaff : people;
  const whitecardHolders = siteStaff.filter((person) =>
    holdsWhitecard(data.qualifications, person.id)
  ).length;

  const expired = filtered.filter((qualification) => qualification.status === 'Expired');
  const expiring90 = filtered.filter(
    (qualification) =>
      qualification.days !== null && qualification.days >= 0 && qualification.days <= 90
  );
  const noEvidence = filtered.filter((qualification) => qualification.att === 0);

  const qualificationOptions = sortCountsDesc(
    countBy(scoped, (qualification) => qualification.name)
  ).map(([value, count]) => ({ value, count }));

  const columns = useMemo(() => {
    if (selectedNames.length > 0) {
      return selectedNames
        .slice()
        .sort((left, right) => left.localeCompare(right))
        .map((name) => ({ label: name, match: (qualification: HrQualification) => qualification.name === name }));
    }
    return CORE_TICKETS.map((ticket) => ({
      label: ticket.label,
      match: (qualification: HrQualification) => ticket.pattern.test(qualification.name),
    }));
  }, [selectedNames]);

  const effectiveTicket = activeTicket && columns.some((column) => column.label === activeTicket)
    ? activeTicket
    : null;
  const activeColumn = columns.find((column) => column.label === effectiveTicket);

  const departments = sortCountsDesc(
    countBy(matrixStaff, (person) => person.dept)
  ).map(([label]) => label);

  const matrixRows = departments.map((department) => {
    const departmentStaff = matrixStaff.filter(
      (person) => (person.dept || 'Not recorded') === department
    );
    return {
      department,
      staff: departmentStaff.length,
      values: columns.map((column) => {
        const holders = departmentStaff.filter((person) =>
          scoped.some(
            (qualification) =>
              qualification.personId === person.id && column.match(qualification)
          )
        ).length;
        return percent(holders, departmentStaff.length);
      }),
    };
  });

  const statuses = ['Expired', 'Due ≤30d', 'Due 31–90d', 'Current', 'No expiry'];
  const statusItems = statuses.map((status) => ({
    key: status,
    label: status,
    value: filtered.filter((qualification) => qualification.status === status).length,
    color:
      status === 'Expired'
        ? 'var(--destructive)'
        : status === 'Due ≤30d'
          ? 'var(--warning)'
          : status === 'Due 31–90d'
            ? 'var(--warning)'
            : status === 'Current'
              ? 'var(--success)'
              : 'var(--muted-foreground)',
  }));

  const months = next12Months(asOf);
  const forecast = months.map((month) => ({
    label: month.label,
    full: month.full,
    value: filtered.filter((qualification) => qualification.expiry?.startsWith(month.key)).length,
  }));

  const roleByPerson = new Map(people.map((person) => [person.id, person.role]));

  const registerRows = activeColumn ? scoped.filter(activeColumn.match) : filtered;

  const registerColumns: HrColumn<HrQualification>[] = [
    { key: 'person', header: 'Employee', value: (row) => row.person },
    { key: 'dept', header: 'Department', value: (row) => row.dept || '—' },
    {
      key: 'role',
      header: 'Job role',
      value: (row) => roleByPerson.get(row.personId) || 'Not recorded',
      render: (row) => roleByPerson.get(row.personId) || <HrStatusPill tone="muted">Not recorded</HrStatusPill>,
    },
    { key: 'name', header: 'Qualification', value: (row) => row.name },
    {
      key: 'expiry',
      header: 'Expiry',
      value: (row) => row.expiry || '',
      render: (row) => formatSydneyDateShort(row.expiry) || '—',
    },
    {
      key: 'status',
      header: 'Status',
      value: (row) => row.status,
      render: (row) => (
        <HrStatusPill tone={qualificationStatusTone(row.status)}>{row.status}</HrStatusPill>
      ),
    },
    {
      key: 'att',
      header: 'Evidence',
      align: 'right',
      value: (row) => row.att,
      render: (row) =>
        row.att > 0 ? (
          <HrStatusPill tone="link">{row.att} file{row.att === 1 ? '' : 's'}</HrStatusPill>
        ) : (
          <HrStatusPill tone="warning">None</HrStatusPill>
        ),
    },
  ];

  const renewableWithoutExpiry = filtered.filter(
    (qualification) => !qualification.expiry && RENEWABLE_NO_EXPIRY.test(qualification.name)
  ).length;

  const exportMatrix = async (format: 'csv' | 'xlsx') => {
    if (!canExport || !(await checkExport())) return;
    setExporting(format);
    try {
      const headers = ['Department', 'Staff', ...columns.map((column) => `${column.label} (%)`)];
      const rows = matrixRows.map((row) => [
        row.department,
        row.staff,
        ...row.values,
      ]);
      const filename = exportFilename(
        'Ticket coverage matrix',
        filters,
        asOf,
        format === 'csv' ? 'csv' : 'xlsx'
      );
      if (format === 'csv') downloadCsv(filename, headers, rows);
      else downloadXlsx(filename, 'Ticket coverage matrix', headers, rows);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Qualification records',
            value: filtered.length,
            detail: `${new Set(filtered.map((row) => row.personId)).size} people`,
            icon: <Ticket className="h-5 w-5" />,
          },
          {
            label: 'Site staff with Whitecard',
            value: percent(whitecardHolders, siteStaff.length),
            unit: '%',
            detail: `${siteStaff.length - whitecardHolders} not on record`,
            tone: percent(whitecardHolders, siteStaff.length) < 80 ? 'danger' : 'success',
            icon: <ShieldCheck className="h-5 w-5" />,
          },
          {
            label: 'Expired',
            value: expired.length,
            tone: expired.length > 0 ? 'danger' : 'success',
            icon: <ShieldAlert className="h-5 w-5" />,
          },
          {
            label: 'Expiring ≤ 90 days',
            value: expiring90.length,
            tone: expiring90.length > 0 ? 'warning' : 'success',
            icon: <ShieldAlert className="h-5 w-5" />,
          },
          {
            label: 'No evidence attached',
            value: noEvidence.length,
            detail: `${percent(noEvidence.length, filtered.length)}% of records`,
            tone: noEvidence.length > 0 ? 'warning' : 'success',
            icon: <FileWarning className="h-5 w-5" />,
          },
          {
            label: 'Renewable tickets with no expiry',
            value: renewableWithoutExpiry,
            detail: 'Cannot be tracked for renewal',
            tone: renewableWithoutExpiry > 0 ? 'warning' : 'primary',
            icon: <Users className="h-5 w-5" />,
          },
        ]}
      />

      <div className="max-w-md">
        <FilterSelect
          label="Qualification"
          options={qualificationOptions}
          selected={selectedNames}
          onChange={(values) => {
            setSelectedNames(values);
            setActiveTicket(null);
          }}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Ticket coverage matrix</CardTitle>
              <CardDescription>
                {siteStaff.length > 0 ? 'Site and yard staff' : 'All active staff'} · % holding
                each {selectedNames.length ? 'selected qualification' : 'core ticket'} · select a
                cell to list holders
              </CardDescription>
            </div>
            {canExport ? (
              <HrExportButtons
                onExport={exportMatrix}
                disabled={exporting !== null}
                exporting={exporting}
              />
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <div className="w-full overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="px-3 py-2 text-left font-semibold text-foreground">Department</th>
                  <th className="px-3 py-2 text-right font-semibold text-foreground">Staff</th>
                  {columns.map((column) => (
                    <th
                      key={column.label}
                      className="px-2 py-2 text-center text-xs font-semibold text-foreground"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixRows.map((row) => (
                  <tr key={row.department} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 whitespace-nowrap text-foreground">
                      {row.department}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {row.staff}
                    </td>
                    {row.values.map((value, index) => {
                      const column = columns[index];
                      const intensity = value >= 75 ? 55 : value >= 50 ? 38 : value >= 25 ? 24 : value > 0 ? 12 : 0;
                      const active = effectiveTicket === column.label;
                      return (
                        <td key={column.label} className="p-0.5">
                          <button
                            type="button"
                            onClick={() =>
                              setActiveTicket(active ? null : column.label)
                            }
                            title={`${row.department} · ${column.label}: ${value}% of ${row.staff}`}
                            className="h-9 w-full min-w-[54px] rounded-sm text-center text-xs font-medium tabular-nums text-foreground transition-colors hover:ring-1 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            style={{
                              backgroundColor: intensity
                                ? `color-mix(in srgb, var(--chart-3) ${intensity}%, transparent)`
                                : undefined,
                            }}
                          >
                            {value ? `${value}%` : '·'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {matrixRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + 2}
                      className="px-3 py-8 text-center text-sm text-muted-foreground"
                    >
                      No records in the current filter
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {[12, 24, 38, 55].map((intensity, index) => (
              <span key={intensity} className="inline-flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{
                    backgroundColor: `color-mix(in srgb, var(--chart-3) ${intensity}%, transparent)`,
                  }}
                />
                {['<25%', '25–49%', '50–74%', '75%+'][index]}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Register status</CardTitle>
            <CardDescription>Every record by expiry state</CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList items={statusItems} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-7">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Expiry forecast</CardTitle>
            <CardDescription>Tickets expiring in each of the next 12 months</CardDescription>
          </CardHeader>
          <CardContent>
            <HrColumnChart data={forecast} color="var(--warning)" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">
                {effectiveTicket ? `Holders · ${effectiveTicket}` : 'Qualification register'}
              </CardTitle>
              <CardDescription>
                Search a person or ticket · sorted by soonest expiry
              </CardDescription>
            </div>
            {effectiveTicket ? (
              <button
                type="button"
                onClick={() => setActiveTicket(null)}
                className="rounded-full border border-link bg-selection px-2.5 py-0.5 text-xs font-medium text-link"
              >
                {effectiveTicket} ✕
              </button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <HrDataTable
            rows={registerRows}
            columns={registerColumns}
            initialSort={{ key: 'expiry', direction: 'asc' }}
            searchPlaceholder="Search employee, department or qualification…"
            emptyLabel="No qualifications found"
            viewName={effectiveTicket ? `Holders - ${effectiveTicket}` : 'Qualification register'}
            filters={filters}
            asOf={asOf}
            canExport={canExport}
            onBeforeExport={checkExport}
            rowKey={(row) => row.id}
          />
        </CardContent>
      </Card>

      {renewableWithoutExpiry > 0 ? (
        <p className="text-xs text-muted-foreground">
          {renewableWithoutExpiry} renewable tickets (Working at Heights, Forklift, First Aid and
          similar) have no expiry date recorded, so they cannot be tracked for renewal.
        </p>
      ) : null}
    </div>
  );
}
